import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleHelp, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Switch } from '../../ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { settingsApi, SystemSetting } from '@/api/settings';
import { SettingEditor } from './SettingEditor';

interface SettingCardProps {
  setting: SystemSetting;
  canEdit: boolean;
}

const KEY_LABELS: Record<string, string> = {
  'system.maintenance.mode': 'Modo mantenimiento',
  'system.maintenance.enabled': 'Modo mantenimiento',
  'system.maintenance.message': 'Mensaje mantenimiento',
  'security.session.timeoutMinutes': 'Sesion (minutos)',
  'security.login.maxAttempts': 'Intentos de login',
  'security.login.blockDurationMinutes': 'Duracion de bloqueo (minutos)',
  'security.magicLink.expiryHours': 'Magic link (horas)',
  'ui.support.contactEmail': 'Correo de soporte',
  'ui.support.phoneNumber': 'Telefono de soporte',
  'ui.theme.primaryColor': 'Color primario',
  REGISTRATION_ATTENDEE_ENABLED: 'Registro de asistentes',
  REGISTRATION_SPEAKER_ENABLED: 'Registro de ponentes',
  REGISTRATION_ATTENDEE_DEADLINE: 'Fecha límite de asistentes',
  REGISTRATION_SPEAKER_DEADLINE: 'Fecha límite de ponentes',
  RATE_LIMIT_CURP_MAX: 'Max validación CURP',
  RATE_LIMIT_SIAE_MAX: 'Max validación SIAE',
  RATE_LIMIT_LOGIN_MAX: 'Max login / submit',
  RATE_LIMIT_TRACKING_MAX: 'Max tracking',
  RATE_LIMIT_RESEND_MAX: 'Max resend',
};

const KEY_DESCRIPTIONS: Record<string, string> = {
  'security.login.blockDurationMinutes':
    'Tiempo que dura el bloqueo tras exceder intentos fallidos (5-1440)',
  REGISTRATION_ATTENDEE_ENABLED: 'Habilita o bloquea el registro publico para asistentes.',
  REGISTRATION_SPEAKER_ENABLED: 'Habilita o bloquea el registro publico para ponentes.',
  REGISTRATION_ATTENDEE_DEADLINE: 'La fecha se evalúa con horario local de México y se guarda en UTC. Déjalo vacío si no quieres límite.',
  REGISTRATION_SPEAKER_DEADLINE: 'La fecha se evalúa con horario local de México y se guarda en UTC. Déjalo vacío si no quieres límite.',
};

const RATE_LIMIT_HELP_TEXT: Record<string, { title: string; lines: string[] }> = {
  RATE_LIMIT_CURP_MAX: {
    title: 'Validación CURP',
    lines: [
      'Protege la validación de CURP en el registro público.',
      'Si queda muy bajo, usuarios legítimos pueden bloquearse en redes compartidas.',
      'Si se sube demasiado, aumenta la exposición a abuso automatizado.',
    ],
  },
  RATE_LIMIT_SIAE_MAX: {
    title: 'Validación SIAE',
    lines: [
      'Protege la validación de matrícula/SIAE.',
      'Si queda muy bajo, puede bloquear registros reales en laboratorios o redes institucionales.',
      'Si se sube demasiado, aumenta el volumen de consultas al servicio externo.',
    ],
  },
  RATE_LIMIT_LOGIN_MAX: {
    title: 'Login / Submit',
    lines: [
      'Protege el acceso administrativo y el envío final del registro.',
      'Si queda muy bajo, puede bloquear operación legítima en aperturas masivas.',
      'Si se sube demasiado, reduce la contención frente a intentos masivos.',
    ],
  },
  RATE_LIMIT_TRACKING_MAX: {
    title: 'Tracking',
    lines: [
      'Protege la consulta de folios y seguimiento.',
      'Si queda muy bajo, afecta staff o grupos que consultan varios folios.',
      'Si se sube demasiado, facilita abuso de consultas automatizadas.',
    ],
  },
  RATE_LIMIT_RESEND_MAX: {
    title: 'Resend',
    lines: [
      'Protege el reenvío de correos y credenciales.',
      'Si queda muy bajo, dificulta soporte operativo legítimo.',
      'Si se sube demasiado, aumenta el riesgo de abuso del servicio de correo.',
    ],
  },
};

function renderSettingValue(setting: SystemSetting): string {
  if (setting.value === null || setting.value === undefined) return 'Sin valor';
  if (
    (setting.key === 'REGISTRATION_ATTENDEE_DEADLINE' || setting.key === 'REGISTRATION_SPEAKER_DEADLINE') &&
    String(setting.value).trim() === ''
  ) {
    return 'Sin límite';
  }
  if (setting.type === 'BOOLEAN') return setting.value ? 'Activado' : 'Desactivado';
  if (setting.type === 'DATETIME') return new Date(String(setting.value)).toLocaleString();
  if (setting.key === 'REGISTRATION_ATTENDEE_DEADLINE' || setting.key === 'REGISTRATION_SPEAKER_DEADLINE') {
    const parsed = new Date(String(setting.value));
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }
  if (typeof setting.value === 'object') return JSON.stringify(setting.value);
  return String(setting.value);
}

export function SettingCard({ setting, canEdit }: SettingCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();
  const normalizedKey = setting.key.toLowerCase().replace(/\s+/g, '');
  const isMaintenanceDomain = normalizedKey.includes('maintenance');
  const isMaintenanceToggle =
    isMaintenanceDomain &&
    (normalizedKey.includes('mode') ||
      normalizedKey.includes('enabled') ||
      setting.type === 'BOOLEAN');
  const isMaintenanceMessage =
    isMaintenanceDomain &&
    (normalizedKey.includes('message') || setting.type === 'STRING');
  const rateLimitHelp = RATE_LIMIT_HELP_TEXT[setting.key];

  const toggleMutation = useMutation({
    mutationFn: async (newValue: boolean) =>
      settingsApi.updateSetting(setting.key, {
        scope: setting.scope,
        editionId: setting.editionId,
        eventId: setting.eventId,
        value: newValue,
        type: setting.type,
        description: setting.description ?? undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Modo mantenimiento actualizado.');
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Error desconocido';
      toast.error(`No fue posible actualizar: ${message}`);
    },
  });

  const handleMaintenanceToggle = (checked: boolean) => {
    if (checked === Boolean(setting.value)) return;
    const accepted = window.confirm(
      'Vas a cambiar el modo mantenimiento. ¿Deseas continuar?'
    );
    if (!accepted) return;
    toggleMutation.mutate(checked);
  };

  return (
    <Card className="shadow-sm border-gray-200 rounded-xl">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold">
                {KEY_LABELS[setting.key] ?? setting.key}
              </CardTitle>
              {rateLimitHelp ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
                      aria-label={`Ayuda sobre ${KEY_LABELS[setting.key] ?? setting.key}`}
                    >
                      <CircleHelp className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    sideOffset={8}
                    className="z-50 max-w-[280px] rounded-md bg-slate-800 px-3 py-2 text-left text-xs leading-relaxed text-white shadow-lg"
                  >
                    <div className="space-y-2">
                      <p className="font-semibold">{rateLimitHelp.title}</p>
                      {rateLimitHelp.lines.map((line) => (
                        <p key={line}>{line}</p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </div>
            {(setting.description || KEY_DESCRIPTIONS[setting.key]) && (
              <p className="text-sm text-gray-600 leading-relaxed">
                {setting.description || KEY_DESCRIPTIONS[setting.key]}
              </p>
            )}
          </div>
          <Badge
            variant="secondary"
            className={setting.scope === 'GLOBAL' ? '!bg-[#1e3a5f] !text-white !text-xs !font-semibold' : '!bg-[#c9a227] !text-white !text-xs !font-semibold'}
          >
            {setting.scope}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {canEdit && isMaintenanceMessage && !isEditing && (
          <div className="mb-3 flex justify-end">
            <Button
              size="icon"
              variant="ghost"
              className="h-9 w-9 rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-[#1e3a5f] hover:bg-[#1e3a5f]/10 hover:text-[#1e3a5f]"
              onClick={() => setIsEditing(true)}
              aria-label="Editar mensaje"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        )}
        {isEditing ? (
          <SettingEditor
            setting={setting}
            onCancel={() => setIsEditing(false)}
            onSave={() => setIsEditing(false)}
          />
        ) : isMaintenanceToggle ? (
          <div className="flex items-center justify-between gap-4 p-4 border rounded-lg bg-muted/50">
            <div className="text-sm text-gray-800 font-medium">
              {Boolean(setting.value) ? 'Activado' : 'Desactivado'}
            </div>
            <div className="flex items-center gap-2">
              {toggleMutation.isPending && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
              <Switch
                className="!h-8 !w-14 !rounded-full !border !border-slate-300 data-[state=checked]:!bg-[#1e3a5f] data-[state=unchecked]:!bg-slate-300 [&_[data-slot=switch-thumb]]:!size-6 [&_[data-slot=switch-thumb]]:!rounded-full [&_[data-slot=switch-thumb]]:!bg-white [&_[data-slot=switch-thumb]]:shadow-sm [&_[data-slot=switch-thumb][data-state=unchecked]]:!translate-x-[2px] [&_[data-slot=switch-thumb][data-state=checked]]:!translate-x-[30px]"
                checked={Boolean(setting.value)}
                onCheckedChange={handleMaintenanceToggle}
                disabled={!canEdit || toggleMutation.isPending}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 p-4 border rounded-lg bg-muted/50">
            <div className="text-sm text-gray-800 break-all">{renderSettingValue(setting)}</div>
            {canEdit && !isMaintenanceMessage && (
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9 rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-[#1e3a5f] hover:bg-[#1e3a5f]/10 hover:text-[#1e3a5f]"
                onClick={() => setIsEditing(true)}
                aria-label={`Editar ${KEY_LABELS[setting.key] ?? setting.key}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
